import { saveTemplateThunk } from '@/common/reducers/CampaignThunks'
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook'
import BulkButton from '@extension/context/content/components/bt-bulk-dashboard/bulk-button'
import { useState } from 'react'
import SaveNewTemplateModal from './save-new-template-modal'
import { DBTemplateThunks, isTempId } from "@common-reducers/DBServiceThunks";
import { setActiveBulkView } from '@common-reducers/CampaignReducer';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { AlertCircle, Trash2 } from 'lucide-react';

type Props = {
    templateId?: any;
    /** Called after a successful save — e.g. the redesign shell routes back to
     *  the origin (the wizard) instead of stranding the user on the editor. */
    onSaved?: () => void;
}

export default function TemplateSaveButtons({ templateId, onSaved }: Props) {
    const dispatch = useAppDispatch();
    const activeBulkView = useAppSelector((state) => state.CampaignReducer.activeBulkView);
    const editorContext = useAppSelector((state: any) => state.ScheduledMessageEditorReducer.editorContext);
    const hasUnsavedChanges = useAppSelector((state) => state.ScheduledMessageEditorReducer.hasUnsavedChanges);
    const message = useAppSelector((state: any) => state.ScheduledMessageEditorReducer.message || '');
    const isPoll = useAppSelector((state: any) => !!state.ScheduledMessageEditorReducer.isPoll);
    const poll = useAppSelector((state: any) => state.ScheduledMessageEditorReducer.poll);
    const [isSaveNewTemplateModalOpen, setIsSaveNewTemplateModalOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

    const [isEditMode, setEditMode] = useState<boolean>(activeBulkView === "campaignNewTemplateViewFromManagement"
        || activeBulkView === "campaignNewTemplateViewFromNewCampaignView"
        || activeBulkView === "campaignNewTemplateViewFromWhatsappView");
    const isMessageEmpty = !message || !message.trim();

    // A poll is saveable when it has a non-empty question AND at least
    // 2 options with non-empty text (matches WA's own poll requirements).
    const validOptionCount = Array.isArray(poll?.options)
        ? poll.options.filter((o: any) => typeof o?.text === 'string' && o.text.trim().length > 0).length
        : 0;
    const isPollValid = isPoll
        && typeof poll?.question === 'string'
        && poll.question.trim().length > 0
        && validOptionCount >= 2;

    // Save button is disabled only when BOTH the text message is empty AND
    // (we're not in poll mode OR the poll is incomplete). Polls don't have
    // a text body, so the message-empty check alone wrongly blocked them.
    const isSaveDisabled = isPoll ? !isPollValid : isMessageEmpty;

    const handleSaveTemplate = () => {
        dispatch(saveTemplateThunk()).unwrap().then(() => onSaved?.()).catch(() => { /* keep the editor open on failure */ });
    }

    const handleDeleteTemplate = () => {
        dispatch(DBTemplateThunks.patch({
            entity: {
                _id: templateId,
                deleted: true,
            }
        }))
        dispatch(setActiveBulkView("campaignManagementView"))
        setIsDeleteDialogOpen(false)
    }

    return (
        <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
        }}>
            {/* Delete button on the left */}
            {templateId && !isTempId(templateId) && (
                <Button
                    variant="outline"
                    size="lg"
                    onClick={() => setIsDeleteDialogOpen(true)}
                    className="h-11 px-6 rounded-lg font-medium text-sm border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-red-50 hover:border-red-300 hover:text-red-600 dark:hover:bg-red-950/30 dark:hover:border-red-500 dark:hover:text-red-400 transition-all duration-200"
                >
                    <Trash2 className="h-4 w-4" />
                    Delete
                </Button>
            )}

            {/* Spacer to push save button to the right when no delete button */}
            {(!templateId || isTempId(templateId)) && <div />}

            {/* Save buttons on the right */}
            {templateId && editorContext === 'scheduleCampaign' && (
                <div>
                    {activeBulkView !== 'campaignManagementView' && // hide for campaign summary (in manage campaigns)
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span style={{ display: 'inline-block' }}>
                                    <BulkButton
                                        onClick={handleSaveTemplate}
                                        buttonString="Save"
                                        disabled={!hasUnsavedChanges}
                                    />
                                </span>
                            </TooltipTrigger>
                            {!hasUnsavedChanges && (
                                <TooltipContent>
                                    All changes are already saved
                                </TooltipContent>
                            )}
                        </Tooltip>
                    }
                </div>
            )}
            {!templateId && isEditMode && (
                <div>
                    <BulkButton
                        onClick={() => setIsSaveNewTemplateModalOpen(true)}
                        buttonString="Save New Template"
                        disabled={isSaveDisabled}
                    />
                </div>
            )}

            <SaveNewTemplateModal
                isSaveNewTemplateModalOpen={isSaveNewTemplateModalOpen}
                onClose={() => setIsSaveNewTemplateModalOpen(false)}
                onSaved={onSaved}
            />

            {/* Delete Confirmation Dialog */}
            <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
                <DialogContent className="max-w-md p-6 sm:p-8">
                    <DialogHeader className="space-y-3">
                        <DialogTitle className="flex items-center gap-2">
                            <AlertCircle className="h-5 w-5 text-red-500" />
                            Delete template?
                        </DialogTitle>
                        <DialogDescription className="text-base text-muted-foreground">
                            Are you sure you want to delete this template? This action cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="mt-6 flex justify-end gap-2">
                        <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
                            Cancel
                        </Button>
                        <Button variant="destructive" onClick={handleDeleteTemplate}>
                            Delete
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    )
}
